import { useCallback, useEffect, useRef, useState } from "react";
import { useDataProvider, useTranslate } from "ra-core";
import { useNavigate } from "react-router";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import type { CrmDataProvider } from "../providers/types";

type SearchResults = Awaited<ReturnType<CrmDataProvider["globalSearch"]>>;

export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResults>({
    contacts: [],
    companies: [],
    deals: [],
    loads: [],
  });
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dataProvider = useDataProvider<CrmDataProvider>();
  const navigate = useNavigate();
  const translate = useTranslate();

  // Cmd+K / Ctrl+K to open
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults({ contacts: [], companies: [], deals: [], loads: [] });
    }
  }, [open]);

  const runSearch = useCallback(
    (q: string) => {
      if (q.trim().length < 2) {
        setResults({ contacts: [], companies: [], deals: [], loads: [] });
        setLoading(false);
        return;
      }
      setLoading(true);
      dataProvider
        .globalSearch(q)
        .then((r) => setResults(r))
        .catch(() =>
          setResults({ contacts: [], companies: [], deals: [], loads: [] })
        )
        .finally(() => setLoading(false));
    },
    [dataProvider]
  );

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(value), 200);
  };

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const hasResults =
    results.contacts.length > 0 ||
    results.companies.length > 0 ||
    results.deals.length > 0 ||
    results.loads.length > 0;

  const showEmpty = query.trim().length >= 2 && !loading && !hasResults;

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="text-secondary-foreground/70 hover:text-secondary-foreground gap-2 px-3"
        onClick={() => setOpen(true)}
      >
        <Search className="h-4 w-4" />
        <span className="text-sm hidden sm:inline">Search</span>
        <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border border-secondary-foreground/20 bg-secondary-foreground/10 px-1.5 py-0.5 text-xs font-mono text-secondary-foreground/50">
          ⌘K
        </kbd>
      </Button>

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title="Global Search"
        description="Search contacts, companies, deals, and loads"
        commandProps={{ shouldFilter: false }}
      >
        <CommandInput
          placeholder="Search contacts, companies, deals, loads..."
          value={query}
          onValueChange={handleQueryChange}
        />
        <CommandList>
          {showEmpty && (
            <CommandEmpty>No results for &ldquo;{query}&rdquo;</CommandEmpty>
          )}
          {!hasResults && query.trim().length < 2 && (
            <CommandEmpty className="text-muted-foreground text-center">
              Type at least 2 characters to search
            </CommandEmpty>
          )}

          {results.contacts.length > 0 && (
            <CommandGroup
              heading={translate("resources.contacts.name", { smart_count: 2 })}
            >
              {results.contacts.map((c: any) => (
                <CommandItem
                  key={c.id}
                  value={`contact-${c.id}`}
                  onSelect={() => go(`/contacts/${c.id}/show`)}
                >
                  <span className="font-medium">
                    {c.first_name} {c.last_name}
                  </span>
                  {c.company_name && (
                    <span className="ml-2 text-muted-foreground text-xs">
                      {c.company_name}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {results.contacts.length > 0 && results.companies.length > 0 && (
            <CommandSeparator />
          )}

          {results.companies.length > 0 && (
            <CommandGroup
              heading={translate("resources.companies.name", {
                smart_count: 2,
              })}
            >
              {results.companies.map((c: any) => (
                <CommandItem
                  key={c.id}
                  value={`company-${c.id}`}
                  onSelect={() => go(`/companies/${c.id}/show`)}
                >
                  <span className="font-medium">{c.name}</span>
                  {c.sector && (
                    <span className="ml-2 text-muted-foreground text-xs">
                      {c.sector}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {results.companies.length > 0 && results.deals.length > 0 && (
            <CommandSeparator />
          )}

          {results.deals.length > 0 && (
            <CommandGroup
              heading={translate("resources.deals.name", { smart_count: 2 })}
            >
              {results.deals.map((d: any) => (
                <CommandItem
                  key={d.id}
                  value={`deal-${d.id}`}
                  onSelect={() => go(`/deals/${d.id}/show`)}
                >
                  <span className="font-medium">{d.name}</span>
                  {d.stage && (
                    <span className="ml-2 text-muted-foreground text-xs">
                      {d.stage}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {results.deals.length > 0 && results.loads.length > 0 && (
            <CommandSeparator />
          )}

          {results.loads.length > 0 && (
            <CommandGroup
              heading={translate("resources.loads.name", { smart_count: 2 })}
            >
              {results.loads.map((l: any) => (
                <CommandItem
                  key={l.id}
                  value={`load-${l.id}`}
                  onSelect={() => go(`/loads/${l.id}/show`)}
                >
                  <span className="font-medium">#{l.load_number}</span>
                  {l.vendor_company_name && (
                    <span className="ml-2 text-muted-foreground text-xs">
                      {l.vendor_company_name}
                      {l.customer_company_name
                        ? ` → ${l.customer_company_name}`
                        : ""}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
};
